// src/components/BusinessUnitAssessment.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApi } from '../hooks/useApi';
import Navigation from "../components/Navigation";
import { PINNED_SIDEBAR_STICKY, PINNED_PANEL_TOP } from "../components/PinnedNavigation";
import Card from "../components/Card";
import Button from "../components/Buttons";
import { useApplicationTypes } from "../contexts/ApplicationTypeContext";
import { useRegions } from "../contexts/RegionContext";
import { useVerticals } from "../contexts/VerticalsContext";
import { Upload, FileText, X, CheckCircle, ArrowLeft, ArrowRight, Loader, AlertCircle, Clock, Flag, Search, Lock } from "lucide-react";
import { useUserBases } from "../contexts/UserBaseContext";
import { useDeploymentModels } from "../contexts/DeploymentModelContext";
import { useAutoAdvance } from "../hooks/useAutoAdvance";
import { useReviewAutoSave } from "../hooks/useReviewAutoSave";
import ReviewSaveStatus from "./ReviewSaveStatus";
import AssessmentSectionSidebar from "../components/AssessmentSectionSidebar";
import { groupQuestionsByCategory, buildAssessmentSections } from "../utils/assessmentSections";
import { buildQuestionNumbers, countTopLevelQuestions } from "../utils/questionNumbering";
import { createQuestionVisibility } from "../utils/questionVisibility";
import { buildDirtyResponseEntries } from "../utils/assessmentResponses";
import { filterAndSortReviewItems } from "../utils/assessmentReview";
import QuestionHistoryToggle, { QuestionHistoryPanel } from "../components/QuestionHistoryToggle";
import QuestionGuidanceBox from "../components/shared/QuestionGuidanceBox";
import { useRBAC } from "../hooks/useRBAC";

// Conditional-question helpers bound to the Business Unit question bank's id and child-sort fields.
const {
    buildQuestionMap,
    isQuestionVisible,
    orderQuestionsWithChildren,
    getVisibleQuestions,
    inheritRootCategory,
    collectDescendantAnswersToClear,
    collectDescendantIds,
} = createQuestionVisibility({ idField: 'businessUnitQuestionId', orderField: 'questionOrder' });


// Stable fallback so `applicationData` keeps the same reference across renders when there's no
// navigation state (direct URL / refresh). Using an inline `{}` created a new object every render,
// which the [applicationData] effect below treated as a change — setFormData → re-render → repeat
// (infinite render loop that also starved route transitions like the "View Dashboard" button).
// The assessment DTOs bind UserBase/DeploymentModel as strings, but prefilled
// application values arrive as numbers (only dropdown edits yield strings) —
// a raw number fails model binding with a 400.
const toApiString = (value) => (value === null || value === undefined || value === "" ? null : String(value));

const EMPTY_LOCATION_STATE = {};

// Colors for the read-only review answer badge (Answers section, isReadOnlyReview).
const ANSWER_BADGE_STYLES = {
    'Yes': "bg-green-100 text-green-800 border-green-200",
    'Partially': "bg-yellow-100 text-yellow-800 border-yellow-200",
    'No': "bg-red-100 text-red-800 border-red-200",
    'Not Applicable': "bg-gray-100 text-gray-700 border-gray-200",
};

const BusinessUnitAssessment = () => {
    const location = useLocation();
    const applicationData = location.state || EMPTY_LOCATION_STATE;
    const apiCall = useApi();
    const { user } = useAuth();
    const { hasMinimumRoleAtOrAbove } = useRBAC();
    const navigate = useNavigate();
    const { id: urlAssessmentId } = useParams();
    const [respondentInfo, setRespondentInfo] = useState({ hasRespondents: false, isRespondent: false, ownerUserId: null });

    const [formData, setFormData] = useState({
        businessUnitApplicationId: applicationData.businessUnitApplicationId || 0,
        applicationName: applicationData.applicationName || "",
        applicationTypeId: applicationData.applicationTypeId || "",
        regionId: applicationData.regionId || "",
        verticalId: applicationData.verticalId || "",
        subVerticalId: applicationData.subVerticalId || "",
        userBaseId: applicationData.userBaseId || "",
        deploymentModelId: applicationData.deploymentModelId || "",
        businessUnitId: applicationData.businessUnitId || 0,
    });

    const [step, setStep] = useState('context');
    const forceReviewRef = useRef(false);
    const [isCheckingPartial, setIsCheckingPartial] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [existingStatus, setExistingStatus] = useState(null);
    const [answeredCount, setAnsweredCount] = useState(0);
    const [responses, setResponses] = useState({});
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [dirtyQuestions, setDirtyQuestions] = useState(new Set());
    // Bumped on each answer, note and flag edit. A save clears the question's dirty flag only if
    // no edit landed while it was in flight, so Submit still sends that edit (AX-205).
    const editVersionRef = useRef({});
    const bumpEditVersion = (qId) => {
        const key = String(qId);
        editVersionRef.current[key] = (editVersionRef.current[key] || 0) + 1;
    };
    // A review note's value when its textarea gained focus: blur saves the note only if it changed.
    const reviewNoteFocusRef = useRef({});
    // The latest submitAssessment, so Submit run after pending review saves reads the state they
    // left (dirty flags, uploaded files) rather than the click-time closure.
    const submitAssessmentRef = useRef(null);
    const justCreatedRef = React.useRef(false);
    const [detailConfirmationPending, setDetailConfirmationPending] = useState(false);
    const [notes, setNotes] = useState({});
    const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
    // Attachments already persisted server-side (rehydrated on load), keyed by businessUnitQuestionId.
    const [savedDocuments, setSavedDocuments] = useState({});
    // Which already-saved documents currently have a retype request in flight, keyed by document
    // id — drives the inline spinner and disables that dropdown so a fast series of changes (see
    // handleChangeSavedDocumentType) can't fire overlapping PUTs for the same document.
    const [savingDocType, setSavingDocType] = useState({});
    // Draft free-text value for an already-saved document currently being retyped to a custom
    // ("Other") type, keyed by document id — presence of a key puts that document's retype
    // control into text-input mode instead of the preset dropdown.
    const [otherRetypeDraft, setOtherRetypeDraft] = useState({});
    const [reviewFilter, setReviewFilter] = useState('all');
    const [reviewSort, setReviewSort] = useState('none');
    const [reviewSearch, setReviewSearch] = useState('');
    // Sidebar-jump support for the review step: a click sets pendingJumpQuestionId, which the
    // effect below (defined once the review-step data is available) scrolls to and briefly
    // highlights — kept as a two-step (pending -> scroll) rather than scrolling directly in the
    // click handler so the scroll happens AFTER any filter reset has re-rendered the list (a
    // filtered-out target can't be scrolled to until the filter clears and it's back in the DOM).
    const [pendingJumpQuestionId, setPendingJumpQuestionId] = useState(null);
    const [highlightedQuestionId, setHighlightedQuestionId] = useState(null);
    // Which question the review-step sidebar treats as "active" — unlike highlightedQuestionId
    // (a ~1.5s flash on the card itself), this persists after a jump so the sidebar keeps that
    // category expanded/highlighted and keeps showing its "jump to question N" picker, letting
    // the user keep navigating within the same category instead of it collapsing right away.
    const [currentReviewQuestionId, setCurrentReviewQuestionId] = useState(null);

    // `questionCategory` is unpopulated on this question source in practice — `riskDomain` is the
    // field that actually carries a meaningful per-question grouping. Fall back through
    // questionCategory/domain before giving up and bucketing under "General".
    const categorizedQuestions = useMemo(
        () => questions.map(q => ({
            ...q,
            __category: q.riskDomain || q.questionCategory || q.domain || 'General',
        })),
        [questions]
    );
    // A conditional follow-up takes its top-level parent's category (display only), then the
    // categories are made contiguous and each follow-up is spliced directly after its parent.
    // Grouping runs on the inherited list and before ordering, so a child never splits from its
    // parent (same sequence as EnterpriseAssessment.js). A follow-up whose parent is not in the
    // bank (for example an excluded scope gate) fails open and shows as unconditional.
    const structuredQuestions = useMemo(() => {
        const inherited = inheritRootCategory(categorizedQuestions, '__category');
        const grouped = groupQuestionsByCategory(inherited, '__category');
        return orderQuestionsWithChildren(grouped);
    }, [categorizedQuestions]);
    // Only the questions visible under the current answers. Everything downstream (stepper,
    // Question X of Y, Sections sidebar, answered count, progress, submit gates, review list)
    // derives from this list, so a hidden follow-up is never shown, counted or required.
    const orderedQuestions = useMemo(
        () => getVisibleQuestions(structuredQuestions, responses),
        [structuredQuestions, responses]
    );
    const sections = useMemo(
        () => buildAssessmentSections(orderedQuestions, {
            categoryField: '__category',
            idField: 'businessUnitQuestionId',
            textField: 'questionText',
            responses,
        }),
        [orderedQuestions, responses]
    );
    // The visible list can shrink (a parent answer hides follow-ups, or a resumed assessment's
    // saved index was taken over a longer list); keep the stepper on a real question. Guarded on a
    // non-empty list because the saved index is restored before the questions load.
    useEffect(() => {
        if (orderedQuestions.length > 0 && currentQuestion > orderedQuestions.length - 1) {
            setCurrentQuestion(orderedQuestions.length - 1);
        }
    }, [orderedQuestions.length, currentQuestion]);
    // Total across ALL categories, not just the currently-displayed question — the
    // per-category sidebar lets users jump straight to the last category, so gating on
    // only the current question let assessments reach Review/Submit while earlier
    // categories were still blank.
    const answeredQuestionsCount = useMemo(
        () => orderedQuestions.filter(q => responses[String(q.businessUnitQuestionId)]).length,
        [orderedQuestions, responses]
    );
    // Display numbers: follow-ups take their parent's number plus a letter (168a, 168b) instead
    // of consuming the next numbers, so revealing them never renumbers later questions.
    const questionNumbers = useMemo(
        () => buildQuestionNumbers(orderedQuestions, 'businessUnitQuestionId'),
        [orderedQuestions]
    );
    const topLevelQuestionCount = useMemo(() => countTopLevelQuestions(questionNumbers), [questionNumbers]);

    const reviewQuestions = useMemo(() => filterAndSortReviewItems(
        orderedQuestions.map(q => ({ q, num: questionNumbers.get(q.businessUnitQuestionId) })),
        {
            getId: ({ q }) => String(q.businessUnitQuestionId),
            getText: ({ q }) => q.questionText,
            responses,
            notes,
            flaggedQuestions,
            filter: reviewFilter,
            sort: reviewSort,
            search: reviewSearch,
        }
    ), [orderedQuestions, questionNumbers, reviewFilter, reviewSort, reviewSearch, flaggedQuestions, notes, responses]);

    // Scrolls to (and briefly highlights) a review-step question card once it's actually in the
    // DOM. handleSidebarJumpToQuestion resets any active filter/search that could be hiding the
    // target and sets pendingJumpQuestionId; this effect retries on every reviewQuestions
    // recompute until the target element exists, so the scroll always lands after the filter
    // reset takes effect instead of firing against a stale, still-filtered list.
    useEffect(() => {
        if (!pendingJumpQuestionId) return;
        const el = document.getElementById(`review-q-${pendingJumpQuestionId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHighlightedQuestionId(pendingJumpQuestionId);
        setPendingJumpQuestionId(null);
        const timer = setTimeout(() => setHighlightedQuestionId(null), 1500);
        return () => clearTimeout(timer);
    }, [pendingJumpQuestionId, reviewQuestions]);

    // Called by the review step's Sections sidebar. Clears any filter/search that would hide the
    // target category (sort is left alone — it only reorders, never hides) and queues the scroll;
    // see the effect above for why this is two steps instead of scrolling directly here.
    const handleSidebarJumpToQuestion = (questionId) => {
        setReviewFilter('all');
        setReviewSearch('');
        setPendingJumpQuestionId(questionId);
        setCurrentReviewQuestionId(questionId);
    };

    // Analysis state for async processing
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisProgress, setAnalysisProgress] = useState(0);
    const [analysisError, setAnalysisError] = useState(null);
    const [isAnalysisTimeout, setIsAnalysisTimeout] = useState(false);
    const [analyzingAssessmentId, setAnalyzingAssessmentId] = useState(null);

    const org = JSON.parse(localStorage.getItem("selectedOrganization"));
    if (!org?.organizationId) {
        return;
    }

    // Multi-respondent submit gating: any assigned respondent can submit — no "primary" concept.
    const canSubmitAssessment = !respondentInfo.hasRespondents
        || respondentInfo.isRespondent
        || respondentInfo.ownerUserId === user?.id
        || hasMinimumRoleAtOrAbove('CONTRIBUTOR', 'ORG', parseInt(org.organizationId));

    useEffect(() => {
        let didRun = false;

        const fetchExistingAssessment = async () => {
            if (didRun) return;
            didRun = true;

            if (justCreatedRef.current) {
                justCreatedRef.current = false;
                setIsCheckingPartial(false);
                return;
            }

            try {
                if (!urlAssessmentId && (!formData?.businessUnitApplicationId || !org?.organizationId)) {
                    setIsCheckingPartial(false);
                    return;
                }

                // Use a mutable copy for hydration (formData is const from useState)
                let updatedFormData = { ...formData };

                // Check if partial exists (or fetch by URL ID)
                let existing = null;
                if (urlAssessmentId) {
                    existing = await apiCall("GET", `/businessunitassessments/${urlAssessmentId}/detail`);
                } else {
                    existing = await apiCall(
                        "GET",
                        `/businessunitassessments/partial-assessment?organizationId=${org.organizationId}&businessUnitApplicationId=${updatedFormData.businessUnitApplicationId}`
                    );
                }

                // Hydrate from API response (works for both URL navigation and partial-assessment)
                if (existing) {
                    updatedFormData = {
                        ...updatedFormData,
                        businessUnitApplicationId: existing.businessUnitApplicationId || updatedFormData.businessUnitApplicationId,
                        businessUnitId: existing.businessUnitId || updatedFormData.businessUnitId,
                        applicationName: existing.applicationName || updatedFormData.applicationName,
                        applicationTypeId: existing.applicationTypeId || updatedFormData.applicationTypeId,
                        regionId: existing.regionId || updatedFormData.regionId,
                        verticalId: existing.verticalId || updatedFormData.verticalId,
                        subVerticalId: existing.subVerticalId || updatedFormData.subVerticalId,
                        userBaseId: existing.userBase || existing.userBaseId || updatedFormData.userBaseId,
                        deploymentModelId: existing.deploymentModel || existing.deploymentModelId || updatedFormData.deploymentModelId,
                    };
                    setFormData(updatedFormData);
                }

                // Backwards compatible: fall back to org's verticalId if assessment has none
                if (!updatedFormData.verticalId && org?.verticalId) {
                    updatedFormData.verticalId = org.verticalId;
                }
                if (!updatedFormData.subVerticalId && org?.subVerticalId) {
                    updatedFormData.subVerticalId = org.subVerticalId;
                }

                // If this is an auto-created assessment awaiting detail confirmation,
                // stay on the context form so the user can confirm details
                if (existing && existing.detailConfirmationPending) {
                    setSavedAssessmentId(existing.businessUnitAssessmentId);
                    setDetailConfirmationPending(true);
                    setIsCheckingPartial(false);
                    return; // Stay on 'context' step with pre-filled data
                }

                // Build question filter params - prefer values from updatedFormData
                const params = [];
                const appTypeId = updatedFormData.applicationTypeId;
                const vertId = updatedFormData.verticalId;
                const regId = updatedFormData.regionId;

                if (appTypeId) params.push(`applicationTypeId=${appTypeId}`);
                if (vertId) params.push(`verticalId=${vertId}`);
                if (regId) params.push(`regionId=${regId}`);
                const query = params.length ? `?${params.join("&")}` : "";

                const allQuestions = await apiCall("GET", `/businessunitquestions${query}`);
                let mergedQuestions = Array.isArray(allQuestions) ? allQuestions : [];

                // Load existing or start fresh
                if (existing) {
                    setSavedAssessmentId(existing.businessUnitAssessmentId);
                    setExistingStatus(existing.businessUnitStatus || null);

                    // Load respondent roster for multi-respondent submit gating
                    try {
                        const roster = await apiCall("GET", `/businessunitassessments/${existing.businessUnitAssessmentId}/assignees`);
                        const assignees = roster?.assignees || [];
                        setRespondentInfo({
                            hasRespondents: assignees.length > 0,
                            isRespondent: assignees.some(a => a.userId === user?.id),
                            ownerUserId: roster?.ownerUserId ?? existing.assessmentAssignedToUserId ?? null,
                        });
                    } catch (rosterErr) {}

                    if (Array.isArray(existing.responses) && existing.responses.length > 0) {
                        const previousResponses = {};
                        const previousNotes = {};
                        const previousFlags = new Set();
                        const previousSavedDocs = {};
                        existing.responses.forEach((r) => {
                            const qIdStr = String(r.businessUnitQuestionId);
                            // Flag-only / note-only saves create rows with no answer — don't
                            // count those as answered.
                            if (r.assessmentResponseAnswer) {
                                previousResponses[qIdStr] = r.assessmentResponseAnswer;
                            }
                            if (r.assessmentResponseComments) {
                                previousNotes[qIdStr] = r.assessmentResponseComments;
                            }
                            if (r.isFlagged) {
                                previousFlags.add(qIdStr);
                            }
                            if (Array.isArray(r.documents) && r.documents.length > 0) {
                                previousSavedDocs[qIdStr] = r.documents.map((d) => ({
                                    id: d.businessUnitAssessmentSupportingDocumentId,
                                    name: d.attachmentName,
                                    type: d.attachmentType,
                                    path: d.supportingDocumentPath,
                                }));
                            }
                        });

                        mergedQuestions = mergedQuestions.map((q) => ({
                            ...q,
                            prefilledAnswer: previousResponses[String(q.businessUnitQuestionId)] || "",
                        }));

                        setResponses(previousResponses);
                        setNotes(previousNotes);
                        setFlaggedQuestions(previousFlags);
                        setSavedDocuments(previousSavedDocs);
                        setAnsweredCount(Object.keys(previousResponses).length);

                        // Restore last question index
                        setCurrentQuestion(
                            existing.lastQuestionAnswerIndex != null
                                ? existing.lastQuestionAnswerIndex
                                : Math.min(Object.keys(previousResponses).length, mergedQuestions.length - 1)
                        );
                    } else {
                        setCurrentQuestion(0);
                    }
                } else {
                    setExistingStatus(null);
                    setCurrentQuestion(0);
                }

                setQuestions(mergedQuestions);
                setTimeout(() => {
                    setResponses(prev => ({ ...prev }));
                }, 100);
                setIsCheckingPartial(false);
            } catch (err) {
                setStep("context");
                setIsCheckingPartial(false);
            }
        };

        const timer = setTimeout(fetchExistingAssessment, 400);
        return () => clearTimeout(timer);
    }, [formData?.businessUnitApplicationId, org?.organizationId, urlAssessmentId]);


    // Step restoration logic - only handles setting the step, currentQuestion is set in fetchExistingAssessment
    useEffect(() => {
        // Check URL param FIRST, before any guards, so reassess navigation always works
        // Not stripped from the URL — kept there so a hard refresh of the review URL lands back on
        // review the same way (forceReviewRef is in-memory only and resets on a fresh page load).
        const urlParams = new URLSearchParams(window.location.search);
        const stepParam = urlParams.get('step');
        if (stepParam === 'review') {
            forceReviewRef.current = true;
            setStep("review");
            return;
        }

        // If review was forced via URL param on a previous run, preserve it
        if (forceReviewRef.current) {
            setStep("review");
            return;
        }

        // Run only after questions are loaded
        if (questions.length === 0) return;
        if (detailConfirmationPending) return; // Don't auto-advance; user must confirm details first

        if (existingStatus === "analyzing") {
            setStep("analyzing");
            setIsAnalyzing(true);
            setAnalyzingAssessmentId(urlAssessmentId || savedAssessmentId);
        } else if (existingStatus === "completed") {
            setStep("review");
        } else if (existingStatus === "in_progress") {
            setStep("questions");
            // Don't override currentQuestion - it's already set correctly from lastQuestionAnswerIndex
        } else {
            setStep("questions");
            // Only set currentQuestion to 0 for new assessments (when existingStatus is null)
            if (existingStatus === null) {
                setCurrentQuestion(0);
            }
        }
    }, [existingStatus, questions.length]); // Re-run when data loads

    // Polling useEffect for analysis status
    useEffect(() => {
        if (!isAnalyzing || !analyzingAssessmentId) return;

        let pollCount = 0;
        const maxPolls = 60;
        const pollInterval = 5000;

        const pollStatus = async () => {
            try {
                const statusResponse = await apiCall(
                    "GET",
                    `/businessunitassessments/${analyzingAssessmentId}/analysis-status`
                );

                setAnalysisProgress(Math.round(statusResponse.analysisProgress) || 0);

                if (statusResponse.status?.toLowerCase() === "completed") {
                    setIsAnalyzing(false);
                    setAnalysisProgress(100);
                    try {
                        const generatedRisks = await apiCall(
                            "GET",
                            `/businessunitassessments/${analyzingAssessmentId}/risks`
                        );
                        setRisks(Array.isArray(generatedRisks) ? generatedRisks : []);
                    } catch (err) {}
                    setStep("results");
                    return;
                }

                if (statusResponse.status?.toLowerCase() === "analysisfailed") {
                    setIsAnalyzing(false);
                    setAnalysisError("Analysis failed. Please try again or contact support.");
                    return;
                }

                pollCount++;
                if (pollCount >= maxPolls) {
                    setIsAnalyzing(false);
                    setIsAnalysisTimeout(true);
                    setAnalysisError("Analysis is still in progress. In some cases, this can take 10 minutes or longer. You can safely leave this page — your results will be available when analysis completes.");
                }
            } catch (err) {
                pollCount++;
                if (pollCount >= maxPolls) {
                    setIsAnalyzing(false);
                    setIsAnalysisTimeout(true);
                    setAnalysisError("Analysis is still in progress. In some cases, this can take 10 minutes or longer. You can safely leave this page — your results will be available when analysis completes.");
                }
            }
        };

        pollStatus();
        const intervalId = setInterval(pollStatus, pollInterval);
        return () => clearInterval(intervalId);
    }, [isAnalyzing, analyzingAssessmentId, apiCall]);

    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savedAssessmentId, setSavedAssessmentId] = useState(null);
    const [uploadedDocuments, setUploadedDocuments] = useState({});
    const [prefilled, setPrefilled] = useState(false);
    const [extraAttachments, setExtraAttachments] = useState([]);
    const [extraAttachmentType, setExtraAttachmentType] = useState("");
    const [extraAttachmentOther, setExtraAttachmentOther] = useState("");
    // Per-question audit trail (who answered/changed/uploaded/noted), grouped client-side by
    // businessUnitQuestionId. Fetched ONCE per assessment and refreshed after each save.
    const [questionHistory, setQuestionHistory] = useState({});

    const loadQuestionHistory = useCallback(async (assessmentId) => {
        if (!assessmentId) return;
        try {
            const data = await apiCall("GET", `/assessmentActivityLog?assessmentType=businessunit&assessmentId=${assessmentId}`);
            const grouped = {};
            (Array.isArray(data) ? data : []).forEach((entry) => {
                if (entry.questionId == null) return;
                const key = String(entry.questionId);
                (grouped[key] ||= []).push(entry);
            });
            setQuestionHistory(grouped);
        } catch {
            setQuestionHistory({});
        }
    }, [apiCall]);

    useEffect(() => {
        const assessmentId = urlAssessmentId || savedAssessmentId;
        if (assessmentId) loadQuestionHistory(assessmentId);
    }, [urlAssessmentId, savedAssessmentId, loadQuestionHistory]);

    // After a successful save, move any files that were just uploaded from the "pending" list
    // (uploadedDocuments) into the "saved" list (savedDocuments) using the server's own
    // confirmation — without this a stale File object sits in uploadedDocuments forever and gets
    // resent, RE-uploaded, and RE-logged as a fresh document_uploaded on every later save.
    const applyUploadedDocuments = useCallback((saveResult, questionIds) => {
        const responses = saveResult?.responses;
        if (Array.isArray(responses)) {
            const docsByQuestion = {};
            responses.forEach((r) => {
                if (r?.documents?.length) {
                    docsByQuestion[String(r.businessUnitQuestionId)] = r.documents.map((d) => ({
                        id: d.businessUnitAssessmentSupportingDocumentId,
                        name: d.attachmentName,
                        type: d.attachmentType,
                    }));
                }
            });
            if (Object.keys(docsByQuestion).length > 0) {
                setSavedDocuments((prev) => {
                    const next = { ...prev };
                    Object.entries(docsByQuestion).forEach(([qId, docs]) => {
                        next[qId] = [...(next[qId] || []), ...docs];
                    });
                    return next;
                });
            }
        }
        setUploadedDocuments((prev) => {
            const next = { ...prev };
            questionIds.forEach((qId) => { next[qId] = []; });
            return next;
        });
    }, []);

    // Removes an already-saved (server-persisted) document via a real delete call so the DB row
    // and blob are actually cleaned up, not just hidden client-side.
    const handleRemoveSavedDocument = async (questionId, doc) => {
        const assessmentId = urlAssessmentId || savedAssessmentId;
        if (!assessmentId || !doc?.id) return;
        if (!window.confirm(`Remove "${doc.name}"? This cannot be undone.`)) return;
        const qIdStr = String(questionId);
        try {
            await apiCall("DELETE", `/businessunitassessments/${assessmentId}/documents/${doc.id}`);
            setSavedDocuments((prev) => ({
                ...prev,
                [qIdStr]: (prev[qIdStr] || []).filter((d) => d.id !== doc.id),
            }));
            loadQuestionHistory(assessmentId);
        } catch (err) {
            alert("Error removing document. Please try again.");
        }
    };

    // Retypes an already-saved document without re-uploading it — the per-question "Document
    // Type" dropdown below only ever tags a NEW file being picked, so a document saved on an
    // earlier visit had no way to be corrected.
    const handleChangeSavedDocumentType = async (questionId, doc, newType) => {
        const assessmentId = urlAssessmentId || savedAssessmentId;
        if (!assessmentId || !doc?.id || !newType || newType === doc.type) return;
        if (savingDocType[doc.id]) return; // a change for this document is already in flight
        const qIdStr = String(questionId);
        setSavingDocType((prev) => ({ ...prev, [doc.id]: true }));
        try {
            const updated = await apiCall("PUT", `/businessunitassessments/${assessmentId}/documents/${doc.id}`, { documentType: newType });
            setSavedDocuments((prev) => ({
                ...prev,
                [qIdStr]: (prev[qIdStr] || []).map((d) =>
                    d.id === doc.id ? { ...d, type: updated?.attachmentType ?? newType } : d
                ),
            }));
            loadQuestionHistory(assessmentId);
        } catch (err) {
            alert("Error updating document type. Please try again.");
        } finally {
            setSavingDocType((prev) => {
                const next = { ...prev };
                delete next[doc.id];
                return next;
            });
        }
    };

    // Switches a saved document's retype control from the preset dropdown to a free-text input —
    // matching the "Other" option the upload-side Document Type dropdown already has. Doesn't
    // save on every keystroke like the upload-side one does: retyping a SAVED document is a real
    // backend PUT, not just local state, so the save is deferred to blur/Enter (commitOtherRetype).
    const startOtherRetype = (doc) => {
        const isCustom = doc.type && !["Network Diagram", "Security Policy", "Pen Test Report", "Certification"].includes(doc.type);
        setOtherRetypeDraft((prev) => ({ ...prev, [doc.id]: isCustom ? doc.type : "" }));
    };

    const commitOtherRetype = (questionId, doc) => {
        const value = (otherRetypeDraft[doc.id] || "").trim();
        setOtherRetypeDraft((prev) => {
            const next = { ...prev };
            delete next[doc.id];
            return next;
        });
        if (value) handleChangeSavedDocumentType(questionId, doc, value);
    };

    // contexts
    const applicationTypes = useApplicationTypes() || [];
    const regions = useRegions() || [];
    const { verticals = [] } = useVerticals() || {};
    // Per-question doc type selection — keyed by question id (like `notes`/`responses`), NOT a
    // single shared value. A single shared value here was the bug: picking "Network Diagram" on
    // Q1 left the dropdown (and any upload made without re-picking) showing "Network Diagram" on
    // every later question too, since nothing ever reset it on navigation.
    const [docTypeByQuestion, setDocTypeByQuestion] = useState({});
    const [otherDocTypeByQuestion, setOtherDocTypeByQuestion] = useState({});
    const userBases = useUserBases() || [];
    const deploymentModels = useDeploymentModels() || [];


    const handleExtraAttachmentUpload = (event) => {
        const files = Array.from(event.target.files || []);
        const validFiles = files.filter(file => file.size <= 10 * 1024 * 1024);
        const filesWithType = validFiles.map(f => {
            f.docType = extraAttachmentType === "Other" ? extraAttachmentOther : extraAttachmentType;
            return f;
        });

        setExtraAttachments(prev => [...prev, ...filesWithType]);
        if (event.target) event.target.value = "";
    };

    const removeExtraAttachment = (index) => {
        setExtraAttachments(prev => prev.filter((_, i) => i !== index));
    };


    useEffect(() => {
        if (applicationData) {
            setFormData(prev => ({
                ...prev,
                applicationName: applicationData.applicationName || prev.applicationName,
                applicationTypeId: applicationData.applicationTypeId || prev.applicationTypeId,
                regionId: applicationData.regionId || prev.regionId,
                verticalId: applicationData.verticalId || prev.verticalId,
                subVerticalId: applicationData.subVerticalId || prev.subVerticalId,
                userBaseId: applicationData.userBaseId || prev.userBaseId,
                deploymentModelId: applicationData.deploymentModelId || prev.deploymentModelId,
            }));
        }
    }, [applicationData]);

    // Prefill only once if we come with applicationData
    useEffect(() => {
        if (!applicationData?.businessUnitApplicationId || prefilled) return;

        setFormData(prev => ({
            ...prev,
            applicationName: applicationData.applicationName || prev.applicationName,
            applicationTypeId: applicationData.applicationTypeId || prev.applicationTypeId,
            regionId: applicationData.regionId || prev.regionId,
            verticalId: applicationData.verticalId || prev.verticalId,
            subVerticalId: applicationData.subVerticalId || prev.subVerticalId,
            userBaseId: applicationData.userBaseId || prev.userBaseId,
            deploymentModelId: applicationData.deploymentModelId || prev.deploymentModelId,
        }));
        setPrefilled(true);
    }, [applicationData, prefilled]);

    const isApplicationPreSelected = !!formData.applicationName && applicationData?.isNewApplication !== true;


    // Fetch questions from backend based on selected filters
    const handleContextSubmit = async () => {
        setLoading(true);
        try {
            const params = [];
            if (formData.applicationTypeId) params.push(`applicationTypeId=${formData.applicationTypeId}`);
            if (formData.verticalId) params.push(`verticalId=${formData.verticalId}`);
            if (formData.regionId) params.push(`regionId=${formData.regionId}`);
            if (formData.userBaseId) params.push(`userBaseId=${formData.userBaseId}`);
            if (formData.deploymentModelId) params.push(`deploymentModelId=${formData.deploymentModelId}`);
            const query = params.length ? `?${params.join("&")}` : "";

            const res = await apiCall("GET", `/businessunitquestions${query}`);

            setQuestions(Array.isArray(res) ? res : []);
            setResponses({});
            setCurrentQuestion(0);

            // If confirming details for an auto-created assessment, update it instead of creating
            if (detailConfirmationPending && savedAssessmentId) {
                await apiCall("PUT", `/businessunitassessments/${savedAssessmentId}/confirm-details`, {
                    VerticalId: formData.verticalId || null,
                    SubVerticalId: formData.subVerticalId || null,
                    ApplicationTypeId: formData.applicationTypeId || null,
                    UserBase: toApiString(formData.userBaseId),
                    DeploymentModel: toApiString(formData.deploymentModelId),
                });
                setDetailConfirmationPending(false);
                setStep("questions");
                setLoading(false);
                return;
            }

            // Create the assessment early so it has a persistent ID
            if (!savedAssessmentId) {
                const assessmentPayload = {
                    BusinessUnitId: formData.businessUnitId,
                    BusinessUnitApplicationId: formData.businessUnitApplicationId,
                    OrganizationId: org?.organizationId,
                    AssessmentType: formData.assessmentType || "Initial",
                    Framework: formData.framework || "Default",
                    AssessmentDate: new Date().toISOString(),
                    Status: "in_progress",
                    Progress: 0,
                    ApplicationTypeId: formData.applicationTypeId,
                    // BusinessUnitAssessmentWriteDto binds these as
                    // UserBase/DeploymentModel (not *Id) — the *Id keys were dropped.
                    DeploymentModel: toApiString(formData.deploymentModelId),
                    RegionId: formData.regionId,
                    VerticalId: formData.verticalId,
                    SubVerticalId: formData.subVerticalId,
                    UserBase: toApiString(formData.userBaseId),
                };
                const created = await apiCall("POST", "/businessunitassessments", assessmentPayload);
                const newId = created?.businessUnitAssessmentId || created?.id;
                if (!newId) throw new Error("Assessment creation failed: no ID returned");
                setSavedAssessmentId(newId);
                justCreatedRef.current = true;
                navigate(`/businessunitassessment/${newId}`, { replace: true, state: location.state });
            }

            setStep("questions");
        } catch (err) {
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    };


    // record user answers
    // Changing a parent's answer can hide conditional follow-ups. Every piece of question-scoped
    // state on those follow-ups (answer, note, flag, pending files, dirty marker) is dropped here,
    // so nothing stale is resubmitted if the question is revealed and answered again:
    // buildDirtyResponseEntries sends a dirty question's note and flag even with no answer.
    // Already-saved attachments are kept (they live on the server). The server reconciles the
    // stored answers (prune, or derive No / Not Applicable) on save.
    const handleResponse = (questionId, answer) => {
        const qIdStr = String(questionId);
        bumpEditVersion(qIdStr);
        const nextResponses = { ...responses, [qIdStr]: answer };
        const answerIds = collectDescendantAnswersToClear(
            Number(questionId), questions, responses, nextResponses);
        // collectDescendantAnswersToClear skips descendants with no answer; a follow-up that was
        // only noted, flagged or given a file must still lose that state when it becomes hidden.
        const byId = buildQuestionMap(questions);
        const newlyHiddenIds = collectDescendantIds(Number(questionId), questions)
            .filter(dId => {
                const q = byId.get(dId);
                return isQuestionVisible(q, byId, responses) && !isQuestionVisible(q, byId, nextResponses);
            });
        const clearedIds = Array.from(new Set([...answerIds, ...newlyHiddenIds].map(String)));
        clearedIds.forEach(dId => delete nextResponses[dId]);

        setResponses(nextResponses);
        setDirtyQuestions(prev => {
            const next = new Set(prev);
            next.add(qIdStr);
            clearedIds.forEach(dId => next.delete(dId));
            return next;
        });
        if (clearedIds.length > 0) {
            const omitIds = (prev) => {
                if (!clearedIds.some(dId => dId in prev)) return prev;
                const next = { ...prev };
                clearedIds.forEach(dId => delete next[dId]);
                return next;
            };
            setNotes(omitIds);
            setUploadedDocuments(omitIds);
            setFlaggedQuestions(prev => {
                if (!clearedIds.some(dId => prev.has(dId))) return prev;
                const next = new Set(prev);
                clearedIds.forEach(dId => next.delete(dId));
                return next;
            });
        }
        triggerAutoAdvance();
    };

    // file upload
    const handleDocumentUpload = (event, questionId, docType) => {
        const files = Array.from(event.target.files || []);
        const maxFileSize = 10 * 1024 * 1024;
        const allowedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "image/png",
            "image/jpeg",
            "image/jpg",
            "text/plain",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];

        const validFiles = files.filter((file) => {
            if (file.size > maxFileSize) {
                alert(`File ${file.name} is too large. Max 10MB.`);
                return false;
            }
            if (!allowedTypes.includes(file.type)) {
                alert(`File type ${file.type} not supported for ${file.name}`);
                return false;
            }
            return true;
        });

        setUploadedDocuments((prev) => ({
            ...prev,
            [questionId]: [
                ...(prev[questionId] || []),
                ...files.map((f) => {
                    f.docType = docType;
                    return f;
                }),
            ],
        }));

        // Mark question as dirty when files are uploaded
        setDirtyQuestions(prev => new Set(prev).add(String(questionId)));

        if (event.target) event.target.value = "";
    };

    // Tags only the pending (not-yet-saved) files that have NO type yet when the Document Type
    // selection changes AFTER those files were picked (file-first flow). Files that already carry a
    // deliberately chosen type keep it, EXCEPT when the change is a continuation of the same edit
    // (replacingType: the Other free-text's previous value — each keystroke must carry pending
    // files from the earlier partial text to the fuller one). A blanket retag here previously
    // overwrote every pending
    // file's type with the latest dropdown value, so two files uploaded under different types all
    // saved as the last-selected type.
    const retagPendingDocuments = (qIdStr, resolvedType, replacingType) => {
        const pending = uploadedDocuments[qIdStr];
        if (!pending || pending.length === 0) return;
        setUploadedDocuments(prev => ({
            ...prev,
            [qIdStr]: prev[qIdStr].map((f) => {
                if (!f.docType || (replacingType != null && f.docType === replacingType)) f.docType = resolvedType;
                return f;
            }),
        }));
        setDirtyQuestions(prev => new Set(prev).add(qIdStr));
    };

    const removeDocument = (questionId, index) => {
        setUploadedDocuments((prev) => ({
            ...prev,
            [questionId]: prev[questionId].filter((_, i) => i !== index),
        }));
    };

    // Save only the current question's response (if dirty)
    const saveSingleQuestion = async (questionId, { background = false } = {}) => {
        const qIdStr = String(questionId);
        // background: a Review & Submit edit (useReviewAutoSave). Always sent — a newer edit can
        // re-dirty the question while an earlier save of it is in flight. No full-page spinner (it
        // would replace the review list) and no alert: ReviewSaveStatus shows a failure inline.
        if (!background && !dirtyQuestions.has(qIdStr)) return; // Skip if not dirty
        const editVersion = editVersionRef.current[qIdStr];

        if (!background) setLoading(true);
        try {
            let assessmentId = savedAssessmentId;

            // Update progress
            const progressPayload = {
                Progress: Math.round((answeredQuestionsCount / Math.max(orderedQuestions.length, 1)) * 100),
                LastQuestionAnswerIndex: currentQuestion
            };
            await apiCall("PUT", `/businessunitassessments/${assessmentId}/progress`, progressPayload);

            // Build single response payload. Always sent when dirty — the question may be
            // dirty solely from a flag/note change, which must persist even with no answer.
            const responseArray = buildDirtyResponseEntries({
                dirtyQuestionIds: [qIdStr],
                questions,
                getQuestionId: (x) => x.businessUnitQuestionId,
                responses,
                notes,
                flaggedQuestions,
                buildEntry: ({ questionId, answer, note, isFlagged }) => ({
                    BusinessUnitQuestionId: questionId,
                    AssessmentResponseAnswer: answer,
                    AssessmentResponseComments: note,
                    IsFlagged: isFlagged,
                    BusinessUnitApplicationId: Number(formData.businessUnitApplicationId) || 0,
                    BusinessUnitId: Number(formData.businessUnitId) || 0,
                    OrganizationId: Number(org?.organizationId) || 0,
                }),
            });

            // Build FormData with only this question's files
            const formDataToSend = new FormData();
            formDataToSend.append("responsesJson", JSON.stringify(responseArray));

            const questionDocs = uploadedDocuments[qIdStr] || [];
            questionDocs.forEach((file) => {
                const docType = file.docType || "Unspecified";
                const prefixedFile = new File([file], `Q${qIdStr}_${file.name}`, {
                    type: file.type,
                });
                formDataToSend.append("files", prefixedFile);
                formDataToSend.append("fileTypes", docType);
            });

            const saveResult = await apiCall("POST", `/businessunitassessments/${assessmentId}/responses/batch`, formDataToSend, true);
            loadQuestionHistory(assessmentId);
            applyUploadedDocuments(saveResult, [qIdStr]);

            // Clear dirty flag for this question, unless it was edited again while this save was in
            // flight: that edit is not in this payload.
            if (editVersionRef.current[qIdStr] === editVersion) {
                setDirtyQuestions(prev => {
                    const next = new Set(prev);
                    next.delete(qIdStr);
                    return next;
                });
            }
        } catch (err) {
            if (background) {
                // An earlier in-flight save of this question may have cleared the dirty flag on
                // success; re-mark it so Submit still sends the change.
                setDirtyQuestions(prev => new Set(prev).add(qIdStr));
                throw err;
            }
            alert("Error saving progress. Please try again.");
            throw err; // Re-throw to block navigation
        } finally {
            if (!background) setLoading(false);
        }
    };

    // Review & Submit edits save as they are made, so a reload keeps them (AX-205).
    const { requestSave: requestReviewSave, saveStatus: reviewSaveStatus, saveErrors: reviewSaveErrors, runWhenIdle: runAfterReviewSaves } =
        useReviewAutoSave(
            (qId) => saveSingleQuestion(qId, { background: true }),
            (qId) => orderedQuestions.some((q) => String(q.businessUnitQuestionId) === qId),
        );

    const nextQuestion = async () => {
        const currentQId = orderedQuestions[currentQuestion]?.businessUnitQuestionId;
        if (currentQId) {
            try {
                await saveSingleQuestion(currentQId);
            } catch {
                return; // Block navigation on save failure
            }
        }
        setCurrentQuestion(idx => Math.min(idx + 1, Math.max(orderedQuestions.length - 1, 0)));
    };

    const prevQuestion = async () => {
        const currentQId = orderedQuestions[currentQuestion]?.businessUnitQuestionId;
        if (currentQId) {
            try {
                await saveSingleQuestion(currentQId);
            } catch {
                return; // Block navigation on save failure
            }
        }
        setCurrentQuestion(idx => Math.max(idx - 1, 0));
    };

    // Jump to a specific question from the section sidebar, saving the current question first.
    const jumpToQuestion = async (businessUnitQuestionId) => {
        const currentQId = orderedQuestions[currentQuestion]?.businessUnitQuestionId;
        if (currentQId && currentQId !== businessUnitQuestionId) {
            try {
                await saveSingleQuestion(currentQId);
            } catch {
                return;
            }
        }
        const idx = orderedQuestions.findIndex(q => q.businessUnitQuestionId === businessUnitQuestionId);
        if (idx >= 0) setCurrentQuestion(idx);
    };

    const toggleFlag = (businessUnitQuestionId) => {
        const qId = String(businessUnitQuestionId);
        bumpEditVersion(qId);
        setFlaggedQuestions(prev => {
            const next = new Set(prev);
            if (next.has(qId)) next.delete(qId); else next.add(qId);
            return next;
        });
        setDirtyQuestions(prev => new Set(prev).add(qId));
    };

    // Auto-advance hook (dev-only feature, toggle with Ctrl+Shift+A)
    const { triggerAutoAdvance } = useAutoAdvance({
        nextQuestion,
        currentQuestion,
        totalQuestions: orderedQuestions.length,
        loading
    });

    // Submit assessment
    const submitAssessment = async () => {
        // Leaving review to submit — drop ?step=review so a reload during/after analysis
        // resumes from normal step restoration instead of being forced to review.
        window.history.replaceState({}, '', window.location.pathname);
        setLoading(true);
        try {
            const assessmentPayload = {
                BusinessUnitId: formData.businessUnitId,
                BusinessUnitApplicationId: formData.businessUnitApplicationId,
                OrganizationId: org?.organizationId,
                AssessmentType: formData.assessmentType || "Initial",
                Framework: formData.framework || "Default",
                AssessmentDate: new Date().toISOString(),
                Progress: 100,
                ApplicationTypeId: formData.applicationTypeId,
                DeploymentModel: toApiString(formData.deploymentModelId),
                RegionId: formData.regionId,
                VerticalId: formData.verticalId,
                SubVerticalId: formData.subVerticalId,
                UserBase: toApiString(formData.userBaseId),
            };

            let assessmentId = savedAssessmentId;
            await apiCall("PUT", `/businessunitassessments/${assessmentId}`, assessmentPayload);

            // Build responses array — every dirty question, including ones that are only
            // flagged/annotated and have no answer yet.
            const responseArray = buildDirtyResponseEntries({
                dirtyQuestionIds: dirtyQuestions,
                questions,
                getQuestionId: (x) => x.businessUnitQuestionId,
                responses,
                notes,
                flaggedQuestions,
                buildEntry: ({ questionId, answer, note, isFlagged }) => ({
                    BusinessUnitQuestionId: questionId,
                    AssessmentResponseAnswer: answer,
                    AssessmentResponseComments: note,
                    IsFlagged: isFlagged,
                    BusinessUnitApplicationId: Number(formData.businessUnitApplicationId) || 0,
                    BusinessUnitId: Number(formData.businessUnitId) || 0,
                    OrganizationId: Number(org?.organizationId) || 0,
                }),
            });

            // Prepare FormData for responses + docs
            const formDataToSend = new FormData();
            formDataToSend.append("responsesJson", JSON.stringify(responseArray));

            Object.entries(uploadedDocuments)
                .filter(([qId]) => dirtyQuestions.has(String(qId)))
                .forEach(([qId, docs]) => {
                docs.forEach((file) => {
                    const docType = file.docType || "Unspecified";
                    formDataToSend.append(
                        "files",
                        new File([file], `Q${qId}_${file.name}`, { type: file.type })
                    );
                    formDataToSend.append("fileTypes", docType);
                });
            });

            // Save responses + docs (only if there are dirty responses)
            if (responseArray.length > 0 || dirtyQuestions.size > 0) {
                const saveResult = await apiCall("POST", `/businessunitassessments/${assessmentId}/responses/batch`, formDataToSend, true);
                loadQuestionHistory(assessmentId);
                applyUploadedDocuments(saveResult, [...dirtyQuestions]);
                setDirtyQuestions(new Set());
            }

            // Call AI Analyze API (async)
            try {
                await apiCall("POST", `/businessunitassessments/${assessmentId}/analyze`);
                setAnalyzingAssessmentId(assessmentId);
                setIsAnalyzing(true);
                setAnalysisProgress(0);
                setAnalysisError(null);
                setIsAnalysisTimeout(false);
                setStep("analyzing");
            } catch (analyzeErr) {
                if (analyzeErr?.response?.status === 409) {
                    // Already analyzing - join the existing analysis
                    setAnalyzingAssessmentId(assessmentId);
                    setIsAnalyzing(true);
                    setAnalysisProgress(0);
                    setAnalysisError(null);
                    setIsAnalysisTimeout(false);
                    setStep("analyzing");
                } else {
                    throw analyzeErr;
                }
            }
        } catch (err) {
            alert("Error saving assessment; check console.");
        } finally {
            setLoading(false);
        }
    };
    submitAssessmentRef.current = submitAssessment;


    // isCheckingPartial covers the actual assessment-detail fetch (fetchExistingAssessment) — loading
    // does not (it's unrelated to this fetch in this component). Checked unconditionally, not scoped
    // to a step: the ?step=review URL fast-path (Reassess, View Answers) sets step to 'review'
    // immediately, before the fetch starts, so a check scoped to step === 'context' never fires for
    // that path and the review step used to render with empty data before this ran.
    if (loading || isCheckingPartial) {
        // Keep the page shell (nav + background) in place instead of swapping to a bare
        // full-screen LoadingSpinner — that would flash the whole layout (nav bar, background
        // color) in and out the instant data arrives, which reads as broken rather than loading.
        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                <div className="max-w-4xl mx-auto py-8 px-4 flex items-center justify-center" style={{ minHeight: "60vh" }}>
                    <div className="animate-spin rounded-full h-16 w-16 border-2 border-transparent border-t-accent-cyan border-r-accent-purple" />
                </div>
            </div>
        );
    }

    // CONTEXT STEP
    if (step === 'context') {
        const isDisabled = !formData.applicationTypeId || !formData.regionId || !formData.verticalId || !formData.subVerticalId;

        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                <div className="max-w-4xl mx-auto py-8 px-4">
                    <Card>
                        <h2 className="text-2xl font-bold mb-6">Business Unit Assessment Context</h2>
                        <p className="text-gray-600 mb-8">Provide details about your application to generate tailored security questions</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {isApplicationPreSelected && (
                                <div className="md:col-span-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-center">
                                        <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
                                        <div>
                                            <span className="font-medium text-blue-900">Selected Application: </span>
                                            <span className="text-blue-800">{formData.applicationName}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Application Type */}
                            <div className="mb-4">
                                <label className="block mb-1 text-sm font-medium text-gray-700">Application Type</label>
                                <select
                                    value={formData.applicationTypeId}
                                    onChange={e => setFormData({ ...formData, applicationTypeId: e.target.value })}
                                    className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="">-- Select Application Type --</option>
                                    {applicationTypes.map(a => (
                                        <option key={a.applicationTypeId} value={a.applicationTypeId}>{a.applicationTypeName}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Region */}
                            <div className="mb-4">
                                <label className="block mb-1 text-sm font-medium text-gray-700">Data Residency Region</label>
                                <select
                                    value={formData.regionId}
                                    disabled
                                    className="block w-full p-2 border border-gray-300 rounded-md shadow-sm sm:text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                                >
                                    <option value="">-- Select Region --</option>
                                    {regions.map(r => <option key={r.regionId} value={r.regionId}>{r.regionName}</option>)}
                                </select>
                            </div>

                            {/* Vertical */}
                            <div className="mb-4">
                                <label className="block mb-1 text-sm font-medium text-gray-700">Vertical</label>
                                <select
                                    value={formData.verticalId}
                                    onChange={e => setFormData({ ...formData, verticalId: e.target.value, subVerticalId: "" })}
                                    className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="">-- Select Vertical --</option>
                                    {verticals.map(v => <option key={v.verticalId} value={v.verticalId}>{v.verticalName}</option>)}
                                </select>
                            </div>

                            {/* Sub-Vertical */}
                            <div className="mb-4">
                                <label className="block mb-1 text-sm font-medium text-gray-700">Sub-Vertical</label>
                                <select
                                    value={formData.subVerticalId}
                                    onChange={e => setFormData({ ...formData, subVerticalId: e.target.value })}
                                    className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50"
                                    disabled={!formData.verticalId}
                                >
                                    <option value="">-- Select Sub-Vertical --</option>
                                    {(verticals.find(v => v.verticalId === parseInt(formData.verticalId))?.subVerticals || []).map(sv => (
                                        <option key={sv.subVerticalId} value={sv.subVerticalId}>{sv.subVerticalName}</option>
                                    ))}
                                </select>
                            </div>

                            {/* User Base */}
                            <div className="mb-4">
                                <label className="block mb-1 text-sm font-medium text-gray-700">User Base</label>
                                <select
                                    name="userBaseId"
                                    value={formData.userBaseId}
                                    onChange={e => setFormData({ ...formData, userBaseId: e.target.value })}
                                    className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="">-- Select User Base --</option>
                                    {userBases.map(a => (
                                        <option key={a.userBaseId} value={a.userBaseId}>{a.userBaseTitle}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Deployment Model */}
                            <div className="mb-4">
                                <label className="block mb-1 text-sm font-medium text-gray-700">Deployment Model</label>
                                <select
                                    name="deploymentModelId"
                                    value={formData.deploymentModelId}
                                    onChange={e => setFormData({ ...formData, deploymentModelId: e.target.value })}
                                    className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    <option value="">-- Select Deployment Model --</option>
                                    {deploymentModels.map(dm => (
                                        <option key={dm.deploymentModelId} value={dm.deploymentModelId}>
                                            {dm.deploymentModelTitle}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="mt-8 flex justify-end">
                            <Button onClick={handleContextSubmit} disabled={isDisabled}>
                                Generate Assessment Questions
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // QUESTIONS STEP
    if (step === 'questions') {
        if (!questions || questions.length === 0) {
            return (
                <div className="min-h-screen bg-gray-50">
                    <Navigation />
                    <div className="max-w-4xl mx-auto py-8 px-4">
                        <Card>
                            <div className="text-center py-8">
                                <p className="text-gray-500">No questions found for the selected context.</p>
                                <div className="mt-4">
                                    <Button onClick={() => setStep('context')}>Back</Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            );
        }

        const currentQ = orderedQuestions[currentQuestion] || {};
        const progress = ((currentQuestion + 1) / Math.max(orderedQuestions.length, 1)) * 100;
        const currentQIdStr = String(currentQ.businessUnitQuestionId);
        const isCurrentFlagged = flaggedQuestions.has(currentQIdStr);

        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                {/* No items-start (default stretch), for the same reason as the review step: the sidebar
                    column has to be as tall as the question card for its list to stay pinned. The
                    history panel opts out with self-start so it pins at its own height. */}
                <div className="max-w-7xl mx-auto py-8 px-4 flex gap-6">
                    <div className="hidden lg:block w-64 flex-shrink-0">
                        <AssessmentSectionSidebar
                            sections={sections}
                            currentQuestionId={currentQ.businessUnitQuestionId}
                            onJumpToQuestion={jumpToQuestion}
                            getQuestionId={(q) => q.businessUnitQuestionId}
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                    <Card>
                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-2xl font-bold">Business Unit Security Assessment</h2>
                                <div className="text-sm text-gray-600">Question {questionNumbers.get(currentQ.businessUnitQuestionId)} of {topLevelQuestionCount}</div>
                            </div>

                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                            </div>
                            {currentQ.__category && (
                                <div className="mt-3 text-sm font-medium text-gray-500 uppercase tracking-wide">
                                    {currentQ.__category}
                                </div>
                            )}
                        </div>

                        <div className="mb-8">
                            <div className="flex items-center space-x-4 mb-4">
                                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                                    {currentQ.verticalId
                                        ? (verticals.find(v => v.verticalId === currentQ.verticalId)?.verticalName || 'Base')
                                        : 'Base'}
                                </span>
                                {currentQ.questionFramework && (
                                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-md">{currentQ.questionFramework}</span>
                                )}
                            </div>

                            <div className="flex items-center gap-3 mb-6">
                                <h3 className="text-xl font-semibold flex-1">{currentQ.questionText || 'Question missing'}</h3>
                                <button
                                    type="button"
                                    onClick={() => toggleFlag(currentQ.businessUnitQuestionId)}
                                    className={isCurrentFlagged ? "text-amber-500 hover:text-amber-600" : "text-gray-400 hover:text-gray-600"}
                                    title={isCurrentFlagged ? "Unflag this question" : "Flag this question"}
                                >
                                    <Flag className="h-5 w-5" fill={isCurrentFlagged ? "currentColor" : "none"} />
                                </button>
                            </div>

                            <QuestionGuidanceBox key={currentQ.businessUnitQuestionId} helpText={currentQ.questionHelpCustomerGuidance} />

                            <div className="space-y-3">
                                {['Yes', 'Partially', 'No', 'Not Applicable'].map(option => (
                                    <label key={option} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                        <input
                                            type="radio"
                                            name={`q-${currentQ.businessUnitQuestionId}`}
                                            value={option}
                                            checked={responses[String(currentQ.businessUnitQuestionId)] === option}
                                            onChange={(e) => handleResponse(String(currentQ.businessUnitQuestionId), e.target.value)}
                                            className="mr-3"
                                        />
                                        <span className="text-gray-700">{option}</span>
                                    </label>
                                ))}
                            </div>

                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
                                <textarea
                                    value={notes[currentQIdStr] || ""}
                                    onChange={(e) => {
                                        setNotes(prev => ({ ...prev, [currentQIdStr]: e.target.value }));
                                        setDirtyQuestions(prev => new Set(prev).add(currentQIdStr));
                                    }}
                                    placeholder="Add context or evidence notes for this question..."
                                    className="w-full border rounded-lg p-2 text-sm"
                                    rows={3}
                                />
                            </div>
                        </div>

                        {/* Document Upload */}
                        <div className="mb-8 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                            <h4 className="font-medium text-content-primary mb-3">Supporting Documents</h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Upload relevant documents such as network diagrams, security policies, pen test reports, or certifications.
                            </p>

                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Document Type
                            </label>
                            <select
                                value={docTypeByQuestion[currentQIdStr] || ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDocTypeByQuestion(prev => ({ ...prev, [currentQIdStr]: value }));
                                    if (value !== "Other") {
                                        setOtherDocTypeByQuestion(prev => ({ ...prev, [currentQIdStr]: "" }));
                                        retagPendingDocuments(currentQIdStr, value);
                                    }
                                    // Switching to "Other" retags once the free-text field below has
                                    // something typed into it (an empty "Other" label isn't useful).
                                }}
                                className="w-full border rounded-lg p-2 mb-4"
                            >
                                <option value="">-- Select Type --</option>
                                <option value="Network Diagram">Network Diagram</option>
                                <option value="Security Policy">Security Policy</option>
                                <option value="Pen Test Report">Pen Test Report</option>
                                <option value="Certification">Certification</option>
                                <option value="Other">Other</option>
                            </select>

                            {docTypeByQuestion[currentQIdStr] === "Other" && (
                                <input
                                    type="text"
                                    placeholder="Please specify document type"
                                    value={otherDocTypeByQuestion[currentQIdStr] || ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const prevOtherType = otherDocTypeByQuestion[currentQIdStr] || "";
                                        setOtherDocTypeByQuestion(prev => ({ ...prev, [currentQIdStr]: value }));
                                        retagPendingDocuments(currentQIdStr, value, prevOtherType);
                                    }}
                                    className="w-full border rounded-lg p-2 mb-4"
                                />
                            )}

                            <input
                                type="file"
                                id={`document-upload-${currentQ.businessUnitQuestionId}`}
                                multiple
                                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.xlsx,.xls"
                                onChange={(e) =>
                                    handleDocumentUpload(
                                        e,
                                        currentQ.businessUnitQuestionId,
                                        docTypeByQuestion[currentQIdStr] === "Other"
                                            ? (otherDocTypeByQuestion[currentQIdStr] || "")
                                            : (docTypeByQuestion[currentQIdStr] || "")
                                    )
                                }
                                className="hidden"
                            />
                            <label
                                htmlFor={`document-upload-${currentQ.businessUnitQuestionId}`}
                                className="flex items-center justify-center w-full p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                                <Upload className="h-5 w-5 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-600">Click to upload documents</span>
                            </label>

                            {/* Previously uploaded (server-persisted) documents */}
                            {savedDocuments[currentQ.businessUnitQuestionId] &&
                                savedDocuments[currentQ.businessUnitQuestionId].length > 0 && (
                                    <div className="mt-4">
                                        <h5 className="text-sm font-medium text-content-primary mb-2">
                                            Previously Uploaded:
                                        </h5>
                                        <div className="space-y-2">
                                            {savedDocuments[currentQ.businessUnitQuestionId].map((doc) => (
                                                <div
                                                    key={doc.id}
                                                    className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200"
                                                >
                                                    <div className="flex items-center min-w-0">
                                                        <FileText className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                                                        <span className="text-sm text-blue-800 truncate">{doc.name}</span>
                                                        {/* Retype without re-uploading — corrects the type saved with the
                                                            file, or one left "Unspecified" before this fix existed. */}
                                                        {otherRetypeDraft[doc.id] !== undefined ? (
                                                            <input
                                                                type="text"
                                                                autoFocus
                                                                value={otherRetypeDraft[doc.id]}
                                                                placeholder="Specify document type"
                                                                onChange={(e) => setOtherRetypeDraft(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                                                onBlur={() => commitOtherRetype(currentQ.businessUnitQuestionId, doc)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                                                                className="ml-3 text-xs border border-blue-200 rounded px-1 py-0.5 text-blue-700 bg-white flex-shrink-0 w-32"
                                                            />
                                                        ) : (
                                                            <select
                                                                value={doc.type || ""}
                                                                onChange={(e) => {
                                                                    if (e.target.value === "Other") startOtherRetype(doc);
                                                                    else handleChangeSavedDocumentType(currentQ.businessUnitQuestionId, doc, e.target.value);
                                                                }}
                                                                disabled={!!savingDocType[doc.id]}
                                                                aria-busy={!!savingDocType[doc.id]}
                                                                className={`ml-3 text-xs border border-blue-200 rounded px-1 py-0.5 text-blue-700 bg-white flex-shrink-0 ${
                                                                    savingDocType[doc.id] ? 'opacity-60 cursor-not-allowed' : ''
                                                                }`}
                                                            >
                                                                <option value="">-- Type --</option>
                                                                <option value="Network Diagram">Network Diagram</option>
                                                                <option value="Security Policy">Security Policy</option>
                                                                <option value="Pen Test Report">Pen Test Report</option>
                                                                <option value="Certification">Certification</option>
                                                                <option value="Other">Other</option>
                                                                {doc.type && !["Network Diagram", "Security Policy", "Pen Test Report", "Certification"].includes(doc.type) && (
                                                                    <option value={doc.type}>{doc.type}</option>
                                                                )}
                                                            </select>
                                                        )}
                                                        {savingDocType[doc.id] && (
                                                            <Loader className="h-3.5 w-3.5 ml-1.5 text-blue-500 animate-spin flex-shrink-0" aria-label="Saving" />
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveSavedDocument(currentQ.businessUnitQuestionId, doc)}
                                                        className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
                                                        title="Remove document"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            {/* Uploaded List (pending upload) */}
                            {uploadedDocuments[currentQ.businessUnitQuestionId] &&
                                uploadedDocuments[currentQ.businessUnitQuestionId].length > 0 && (
                                    <div className="mt-4">
                                        <h5 className="text-sm font-medium text-content-primary mb-2">
                                            Uploaded Documents:
                                        </h5>
                                        <div className="space-y-2">
                                            {uploadedDocuments[currentQ.businessUnitQuestionId].map((file, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200"
                                                >
                                                    <div className="flex items-center">
                                                        <FileText className="h-4 w-4 text-green-600 mr-2" />
                                                        <span className="text-sm text-green-800">{file.name}</span>
                                                        <span className="text-xs text-gray-500 ml-2">
                                                            ({file.size ? (file.size / 1024).toFixed(1) : "0"} KB)
                                                        </span>
                                                        {file.docType && (
                                                            <span className="ml-3 text-xs text-blue-600">
                                                                [{file.docType}]
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            setUploadedDocuments((prev) => {
                                                                const copy = { ...prev };
                                                                copy[currentQ.businessUnitQuestionId] = copy[currentQ.businessUnitQuestionId].filter(
                                                                    (_, i) => i !== idx
                                                                );
                                                                return copy;
                                                            });
                                                        }}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                        </div>

                        {currentQuestion === orderedQuestions.length - 1 && answeredQuestionsCount < orderedQuestions.length && (
                            <div className="mb-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                <span>
                                    Please answer every question in all categories before submitting —{" "}
                                    {orderedQuestions.length - answeredQuestionsCount} question
                                    {orderedQuestions.length - answeredQuestionsCount === 1 ? "" : "s"} remaining.
                                </span>
                            </div>
                        )}

                        <div className="flex justify-between">
                            <Button
                                variant="secondary"
                                onClick={prevQuestion}
                                disabled={currentQuestion === 0 || loading}
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" /> Previous
                            </Button>

                            <div className="flex space-x-3">
                                <Button
                                    variant="ghost"
                                    onClick={async () => {
                                        const currentQId = orderedQuestions[currentQuestion]?.businessUnitQuestionId;
                                        if (currentQId) {
                                            try {
                                                await saveSingleQuestion(currentQId);
                                                alert("Business Unit progress saved successfully ✅");
                                            } catch {
                                                // Error already shown in saveSingleQuestion
                                            }
                                        }
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : "Save Progress"}
                                </Button>

                                {currentQuestion === orderedQuestions.length - 1 ? (
                                    <Button
                                        onClick={async () => {
                                            const currentQId = orderedQuestions[currentQuestion]?.businessUnitQuestionId;
                                            if (currentQId) {
                                                try {
                                                    await saveSingleQuestion(currentQId);
                                                } catch {
                                                    return; // Block navigation on save failure
                                                }
                                            }
                                            setStep("review");
                                        }}
                                        disabled={answeredQuestionsCount < orderedQuestions.length || loading}
                                        title={answeredQuestionsCount < orderedQuestions.length
                                            ? `${orderedQuestions.length - answeredQuestionsCount} question${orderedQuestions.length - answeredQuestionsCount === 1 ? "" : "s"} remaining`
                                            : ""}
                                    >
                                        Review & Submit
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={nextQuestion}
                                        disabled={!responses[String(currentQ.businessUnitQuestionId)] || loading}
                                    >
                                        Next <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                )}
                            </div>
                        </div>

                    </Card>
                    </div>

                    {/* History panel: sticky right column, internally scrollable so it stays
                        visible and bounded regardless of log length. */}
                    <div className={`hidden xl:block w-80 flex-shrink-0 self-start sticky ${PINNED_PANEL_TOP}`}>
                        <QuestionHistoryPanel entries={questionHistory[currentQIdStr] || []} />
                    </div>
                </div>
            </div>
        );
    }

    // REVIEW STEP
    if (step === 'review') {
        // A completed assessment always lands here (see step-restoration above). Reassess resets
        // status to in_progress before navigating back to this same step, so this stays false for
        // that flow — completed is the only status this view step can't be reached with editing
        // intended. Safe to read here without an extra loading gate: the top-level `if (loading)`
        // check above already covers the same fetch window this depends on.
        const isReadOnlyReview = existingStatus === 'completed';
        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                {/* No items-start here (default stretch): the sidebar's own content is short,
                    but the review list can run to 50-100+ questions — items-start would cap
                    this column's height at the sidebar's natural height, leaving the sticky
                    nav below with no room in its own parent to stay pinned while the (much
                    taller) question list scrolls past. Stretching this column to match the
                    list's height gives sticky the room it needs to actually stick. */}
                <div className="max-w-7xl mx-auto py-8 px-4 flex gap-6">
                    <div className="hidden lg:block w-64 flex-shrink-0">
                        <AssessmentSectionSidebar
                            sections={sections}
                            currentQuestionId={currentReviewQuestionId}
                            onJumpToQuestion={handleSidebarJumpToQuestion}
                            stickyClassName={PINNED_SIDEBAR_STICKY}
                            getQuestionId={(q) => q.businessUnitQuestionId}
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                    <Card>
                        <h2 className="text-2xl font-bold mb-6">
                            {isReadOnlyReview ? "Assessment Responses" : "Review Your Answers"}
                        </h2>

                        {isReadOnlyReview && (
                            <div className="mb-6 flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                <Lock className="h-4 w-4 flex-shrink-0" />
                                <span>This assessment is completed and shown read-only. Use Reassess to make changes.</span>
                            </div>
                        )}

                        {/* Filter / Sort controls */}
                        <div className="flex flex-wrap gap-3 mb-6">
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={reviewSearch}
                                    onChange={(e) => setReviewSearch(e.target.value)}
                                    placeholder="Search questions, answers, notes..."
                                    className="w-full pl-9 pr-2 py-2 text-sm border rounded-lg"
                                />
                            </div>
                            <select
                                value={reviewFilter}
                                onChange={(e) => setReviewFilter(e.target.value)}
                                className="border rounded-lg p-2 text-sm"
                            >
                                <option value="all">All answers</option>
                                <option value="Yes">Yes</option>
                                <option value="Partially">Partially</option>
                                <option value="No">No</option>
                                <option value="Not Applicable">Not Applicable</option>
                                <option value="flagged">Flagged only</option>
                                <option value="commented">Has notes</option>
                            </select>
                            <select
                                value={reviewSort}
                                onChange={(e) => setReviewSort(e.target.value)}
                                className="border rounded-lg p-2 text-sm"
                            >
                                <option value="none">Default order</option>
                                <option value="flagged-first">Flagged first</option>
                                <option value="answer">Sort by answer</option>
                            </select>
                        </div>

                        <div className="space-y-6">
                            {reviewQuestions.map(({ q, num }) => {
                                const qid = String(q.businessUnitQuestionId);
                                const isFlagged = flaggedQuestions.has(qid);
                                return (
                                <div
                                    key={q.businessUnitQuestionId}
                                    id={`review-q-${q.businessUnitQuestionId}`}
                                    className={`p-4 border rounded-lg transition-shadow ${
                                        highlightedQuestionId === q.businessUnitQuestionId ? 'ring-2 ring-blue-400' : ''
                                    }`}
                                >
                                    <div className="flex justify-between items-center mb-2 gap-4">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <h3
                                                className="font-medium cursor-pointer hover:text-blue-700"
                                                onClick={() => setCurrentReviewQuestionId(q.businessUnitQuestionId)}
                                                title="View this question's history"
                                            >
                                                {num}. {q.questionText}
                                            </h3>
                                            <button
                                                type="button"
                                                disabled={isReadOnlyReview}
                                                onClick={() => { toggleFlag(q.businessUnitQuestionId); requestReviewSave(q.businessUnitQuestionId); }}
                                                className={
                                                    isFlagged
                                                        ? (isReadOnlyReview ? "text-amber-500 cursor-default" : "text-amber-500 hover:text-amber-600")
                                                        : (isReadOnlyReview ? "text-gray-300 cursor-default" : "text-gray-400 hover:text-gray-600")
                                                }
                                                title={isReadOnlyReview ? "" : (isFlagged ? "Unflag this question" : "Flag this question")}
                                            >
                                                <Flag className="h-4 w-4" fill={isFlagged ? "currentColor" : "none"} />
                                            </button>
                                        </div>
                                        <span className="text-sm text-gray-500 flex-shrink-0">{q.__category}</span>
                                    </div>

                                    <QuestionGuidanceBox helpText={q.questionHelpCustomerGuidance} />

                                    {isReadOnlyReview ? (
                                        // A disabled radio group renders every option in the same muted grey —
                                        // the selected one is nearly indistinguishable at a glance. Show the
                                        // saved answer as a single colored badge instead.
                                        <div className="mb-4">
                                            <span className={`inline-block px-3 py-1.5 border rounded-lg text-sm font-medium ${ANSWER_BADGE_STYLES[responses[qid] || q.prefilledAnswer] || "bg-gray-100 text-gray-500 border-gray-200"}`}>
                                                {responses[qid] || q.prefilledAnswer || "Not answered"}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="space-y-2 mb-4">
                                            {['Yes', 'Partially', 'No', 'Not Applicable'].map((option) => {
                                                // Only the live response decides the radio. prefilledAnswer is a load-time snapshot
                                                // that never changes, so OR-ing it in left two radios in the group checked after an
                                                // edit and the dot snapped back to the old answer (AX-203).
                                                const selected = responses[qid] === option;

                                                return (
                                                    <label
                                                        key={option}
                                                        className={`flex items-center p-2 border rounded-lg cursor-pointer ${selected ? 'bg-blue-50 border-blue-400' : 'hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`review-q-${qid}`}
                                                            value={option}
                                                            checked={selected}
                                                            onChange={(e) => {
                                                                handleResponse(qid, e.target.value);
                                                                requestReviewSave(qid);
                                                            }}
                                                            className="mr-2"
                                                        />
                                                        <span>{option}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Notes */}
                                    {reviewSaveStatus[qid] && (
                                        <div className="-mt-2 mb-3">
                                            <ReviewSaveStatus status={reviewSaveStatus[qid]} error={reviewSaveErrors[qid]} onRetry={() => requestReviewSave(qid)} />
                                        </div>
                                    )}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                                        <textarea
                                            value={notes[qid] || ""}
                                            disabled={isReadOnlyReview}
                                            onChange={(e) => {
                                                setNotes(prev => ({ ...prev, [qid]: e.target.value }));
                                                setDirtyQuestions(prev => new Set(prev).add(qid));
                                                bumpEditVersion(qid);
                                            }}
                                            onFocus={(e) => { reviewNoteFocusRef.current[qid] = e.target.value; }}
                                            onBlur={(e) => { if (e.target.value !== reviewNoteFocusRef.current[qid]) requestReviewSave(qid); }}
                                            placeholder="Add context or evidence notes for this question..."
                                            className="w-full border rounded-lg p-2 text-sm"
                                            rows={2}
                                        />
                                        <QuestionHistoryToggle entries={questionHistory[qid] || []} />
                                    </div>

                                    {/* Previously uploaded (server-persisted) documents */}
                                    {savedDocuments[q.businessUnitQuestionId] && savedDocuments[q.businessUnitQuestionId].length > 0 && (
                                        <div className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                                            <h4 className="font-medium text-content-primary mb-2">Previously Uploaded</h4>
                                            <div className="space-y-2">
                                                {savedDocuments[q.businessUnitQuestionId].map((doc) => (
                                                    <div key={doc.id} className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                                                        <div className="flex items-center min-w-0">
                                                            <FileText className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                                                            <span className="text-sm text-blue-800 truncate">{doc.name}</span>
                                                            {isReadOnlyReview ? (
                                                                doc.type && <span className="ml-2 text-xs text-blue-600 flex-shrink-0">[{doc.type}]</span>
                                                            ) : (
                                                                <>
                                                                    {otherRetypeDraft[doc.id] !== undefined ? (
                                                                        <input
                                                                            type="text"
                                                                            autoFocus
                                                                            value={otherRetypeDraft[doc.id]}
                                                                            placeholder="Specify document type"
                                                                            onChange={(e) => setOtherRetypeDraft(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                                                            onBlur={() => commitOtherRetype(q.businessUnitQuestionId, doc)}
                                                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                                                                            className="ml-3 text-xs border border-blue-200 rounded px-1 py-0.5 text-blue-700 bg-white flex-shrink-0 w-32"
                                                                        />
                                                                    ) : (
                                                                        <select
                                                                            value={doc.type || ""}
                                                                            onChange={(e) => {
                                                                                if (e.target.value === "Other") startOtherRetype(doc);
                                                                                else handleChangeSavedDocumentType(q.businessUnitQuestionId, doc, e.target.value);
                                                                            }}
                                                                            disabled={!!savingDocType[doc.id]}
                                                                            aria-busy={!!savingDocType[doc.id]}
                                                                            className={`ml-3 text-xs border border-blue-200 rounded px-1 py-0.5 text-blue-700 bg-white flex-shrink-0 ${
                                                                                savingDocType[doc.id] ? 'opacity-60 cursor-not-allowed' : ''
                                                                            }`}
                                                                        >
                                                                            <option value="">-- Type --</option>
                                                                            <option value="Network Diagram">Network Diagram</option>
                                                                            <option value="Security Policy">Security Policy</option>
                                                                            <option value="Pen Test Report">Pen Test Report</option>
                                                                            <option value="Certification">Certification</option>
                                                                            <option value="Other">Other</option>
                                                                            {doc.type && !["Network Diagram", "Security Policy", "Pen Test Report", "Certification"].includes(doc.type) && (
                                                                                <option value={doc.type}>{doc.type}</option>
                                                                            )}
                                                                        </select>
                                                                    )}
                                                                    {savingDocType[doc.id] && (
                                                                        <Loader className="h-3.5 w-3.5 ml-1.5 text-blue-500 animate-spin flex-shrink-0" aria-label="Saving" />
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                        {!isReadOnlyReview && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveSavedDocument(q.businessUnitQuestionId, doc)}
                                                                className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
                                                                title="Remove document"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {uploadedDocuments[q.businessUnitQuestionId] && uploadedDocuments[q.businessUnitQuestionId].length > 0 && (
                                        <div className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                                            <h4 className="font-medium text-content-primary mb-2">Attachments</h4>
                                            <div className="space-y-2">
                                                {uploadedDocuments[q.businessUnitQuestionId].map((file, fidx) => (
                                                    <div key={fidx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                                                        <div className="flex items-center">
                                                            <FileText className="h-4 w-4 text-green-600 mr-2" />
                                                            <span className="text-sm text-green-800">{file.name}</span>
                                                            {file.docType && <span className="ml-2 text-xs text-blue-600">[{file.docType}]</span>}
                                                        </div>
                                                        {!isReadOnlyReview && (
                                                            <button onClick={() => removeDocument(q.businessUnitQuestionId, fidx)} className="text-red-600 hover:text-red-800">
                                                                <X className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>

                        {/* Extra Attachments Section — not offered read-only: nothing is loaded into
                            extraAttachments on fetch (it only ever holds files staged in this session),
                            so there's nothing to view here for a freshly-opened completed assessment. */}
                        {!isReadOnlyReview && (
                        <div className="mt-8 p-4 border-2 border-dashed border-gray-300 rounded-lg">
                            <h4 className="font-medium text-content-primary mb-2">Extra Supporting Documents</h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Upload any additional files that support the overall assessment.
                            </p>

                            <select
                                value={extraAttachmentType}
                                onChange={(e) => {
                                    setExtraAttachmentType(e.target.value);
                                    if (e.target.value !== "Other") setExtraAttachmentOther("");
                                }}
                                className="w-full border rounded-lg p-2 mb-4"
                            >
                                <option value="">-- Select Document Type --</option>
                                <option value="Network Diagram">Network Diagram</option>
                                <option value="Security Policy">Security Policy</option>
                                <option value="Pen Test Report">Pen Test Report</option>
                                <option value="Certification">Certification</option>
                                <option value="Other">Other</option>
                            </select>

                            {extraAttachmentType === "Other" && (
                                <input
                                    type="text"
                                    placeholder="Specify document type"
                                    value={extraAttachmentOther}
                                    onChange={(e) => setExtraAttachmentOther(e.target.value)}
                                    className="w-full border rounded-lg p-2 mb-4"
                                />
                            )}

                            <input
                                type="file"
                                id="extra-attachments-upload"
                                multiple
                                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.xlsx,.xls"
                                onChange={handleExtraAttachmentUpload}
                                className="hidden"
                            />
                            <label
                                htmlFor="extra-attachments-upload"
                                className="flex items-center justify-center w-full p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                                <Upload className="h-5 w-5 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-600">Click to upload extra files</span>
                            </label>

                            {extraAttachments.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {extraAttachments.map((file, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                                            <div className="flex items-center">
                                                <FileText className="h-4 w-4 text-green-600 mr-2" />
                                                <span className="text-sm text-green-800">{file.name}</span>
                                                {file.docType && <span className="ml-2 text-xs text-blue-600">[{file.docType}]</span>}
                                            </div>
                                            <button onClick={() => removeExtraAttachment(idx)} className="text-red-600 hover:text-red-800">
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        )}

                        {!isReadOnlyReview && answeredQuestionsCount < orderedQuestions.length && (
                            <div className="mt-6 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                <span>
                                    {orderedQuestions.length - answeredQuestionsCount} question
                                    {orderedQuestions.length - answeredQuestionsCount === 1 ? "" : "s"} still{" "}
                                    {orderedQuestions.length - answeredQuestionsCount === 1 ? "needs" : "need"} an answer before you can submit.
                                </span>
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                            {isReadOnlyReview ? (
                                <Button variant="secondary" onClick={() => navigate(-1)}>
                                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                                </Button>
                            ) : (
                                <>
                                    <Button variant="secondary" onClick={() => runAfterReviewSaves(() => {
                                        // Editing resumes — drop ?step=review so a reload lands back on questions
                                        // instead of being forced to review. Waits for pending review saves, so a
                                        // questions-step save can't race one still in flight (ignored while a
                                        // Submit is waiting).
                                        window.history.replaceState({}, '', window.location.pathname);
                                        setStep('questions');
                                    })}>
                                        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Questions
                                    </Button>
                                    <Button
                                        onClick={() => runAfterReviewSaves(() => submitAssessmentRef.current())}
                                        disabled={isAnalyzing || !canSubmitAssessment || answeredQuestionsCount < orderedQuestions.length}
                                        title={
                                            isAnalyzing ? "Assessment is being analyzed"
                                            : !canSubmitAssessment ? "Only an assigned respondent can submit this assessment"
                                            : answeredQuestionsCount < orderedQuestions.length
                                                ? `${orderedQuestions.length - answeredQuestionsCount} question${orderedQuestions.length - answeredQuestionsCount === 1 ? "" : "s"} still need an answer`
                                                : ""
                                        }
                                    >
                                        Submit Assessment
                                    </Button>
                                </>
                            )}
                        </div>
                    </Card>

                    </div>

                    {/* History panel: click a question's title (above) to view it here — mirrors
                        the one-at-a-time step's right-side panel. The `sticky` class has to sit
                        on an INNER div, not this outer w-80 column itself — same reason the
                        Sections sidebar's sticky lives on its inner <nav>, not its own w-64
                        column: this outer column gets stretched to the full (very long) question
                        list's height by the flex row (no items-start, see above), so if IT were
                        the sticky element, it would already span the whole scrollable area and
                        have no room left to "chase" the viewport — the panel would just sit
                        wherever the row started, near the top of the page, instead of tracking
                        scroll position the way the sidebar does. */}
                    <div className="hidden xl:block w-80 flex-shrink-0">
                        <div className={`sticky ${PINNED_PANEL_TOP}`}>
                            {currentReviewQuestionId != null ? (
                                <QuestionHistoryPanel entries={questionHistory[String(currentReviewQuestionId)] || []} />
                            ) : (
                                <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs text-gray-400">
                                    Click a question above to view its history here.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ANALYZING STEP
    if (step === "analyzing") {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                <div className="max-w-4xl mx-auto py-8 px-4">
                    <Card>
                        <div className="text-center py-12">
                            {analysisError ? (
                                <>
                                    {isAnalysisTimeout ? (
                                        <Clock className="h-16 w-16 text-amber-500 mx-auto mb-6" />
                                    ) : (
                                        <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
                                    )}
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                        {isAnalysisTimeout ? "Analysis Taking Longer Than Expected" : "Analysis Failed"}
                                    </h2>
                                    <p className="text-gray-600 mb-8">{analysisError}</p>
                                    <div className="flex justify-center">
                                        <Button
                                            variant="secondary"
                                            onClick={() => navigate('/dashboard')}
                                        >
                                            View Dashboard
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="relative">
                                        <Loader className="h-16 w-16 text-blue-600 mx-auto mb-6 animate-spin" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Analyzing Assessment</h2>
                                    <p className="text-gray-600 mb-2">
                                        Our AI is analyzing your responses and generating risk insights.
                                    </p>
                                    <p className="text-gray-600 mb-6">
                                        This typically takes a few minutes, but in some cases may take 10 minutes or longer.
                                    </p>
                                    <div className="max-w-md mx-auto mb-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm text-gray-500">Progress</span>
                                            <span className="text-sm font-medium text-gray-700">{analysisProgress}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div
                                                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                                                style={{ width: `${analysisProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // RESULTS STEP - Navigate to risks view
    if (step === 'results') {
        navigate(`/businessunitassessment/${savedAssessmentId}/risks`);
        return null;
    }

    return null;
};

export default BusinessUnitAssessment;
